import { useEffect, useState } from 'react';
import { Search, User, ShieldCheck, Mail, Phone, CheckCircle2, X, Crown, Banknote, Headphones, BadgeCheck, Wrench } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState, Input, Select, StatusLine } from '../components/ui';

type Capability = 'finance' | 'support' | 'verification' | 'ops';
const ALL_CAPS: Capability[] = ['finance', 'support', 'verification', 'ops'];

const CAP_META: Record<Capability, { label: string; icon: any; tone: 'amber' | 'blue' | 'green' | 'gray' }> = {
  finance:      { label: 'Finance',      icon: Banknote,   tone: 'amber' },
  support:      { label: 'Support',      icon: Headphones, tone: 'blue' },
  verification: { label: 'Verification', icon: BadgeCheck, tone: 'green' },
  ops:          { label: 'Ops',          icon: Wrench,     tone: 'gray' },
};

type Row = {
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  is_admin: boolean;
  is_super_admin: boolean;
  is_club_admin: boolean;
  admin_capabilities: Capability[];
  identity_verified_at: string | null;
  created_at: string;
  last_sign_in_at: string | null;
};

type Role = 'none' | 'club_admin' | 'super_admin';

function rowToRole(r: Row): Role {
  if (r.is_super_admin) return 'super_admin';
  if (r.is_club_admin) return 'club_admin';
  if (r.is_admin) return 'super_admin'; // legacy pre-split rows
  return 'none';
}

export default function Users({ tier }: { tier: 'super_admin' | 'club_admin' | 'none' }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const canEditRoles = tier === 'super_admin';

  const load = async (search: string | null) => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_users', {
      p_search: search && search.trim() ? search.trim() : null,
      p_limit: 200,
    });
    setLoading(false);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setRows((data as Row[]) ?? []);
  };

  useEffect(() => { load(null); }, []);

  const submitSearch = (e: React.FormEvent) => { e.preventDefault(); load(q); };

  const setRole = async (r: Row, role: Role) => {
    const current = rowToRole(r);
    if (current === role) return;
    const label = role === 'none' ? 'demote to customer' : `set to ${role.replace('_', ' ')}`;
    if (!confirm(`${label[0].toUpperCase() + label.slice(1)}: ${r.email}?`)) return;
    setMsg(null);
    const { error } = await supabase.rpc('admin_set_role', {
      p_user_id: r.user_id,
      p_role: role,
    });
    if (error) {
      setMsg({ kind: 'error', text: error.message });
      return;
    }
    await supabase.rpc('audit_log', {
      p_action: 'role_changed',
      p_target_type: 'profile',
      p_target_id: r.user_id,
      p_metadata: { email: r.email, from: current, to: role } as any,
    });
    setMsg({ kind: 'success', text: `${r.email} → ${role.replace('_', ' ')}` });
    setRows((prev) => prev.map((x) => x.user_id === r.user_id ? {
      ...x,
      is_super_admin: role === 'super_admin',
      is_club_admin:  role === 'club_admin',
      is_admin:       role !== 'none',
      // Mirror the server-side defaults set by admin_set_role.
      admin_capabilities: role === 'super_admin' ? []
        : role === 'club_admin' ? ['support', 'verification', 'ops']
        : [],
    } : x));
  };

  const toggleCapability = async (r: Row, cap: Capability) => {
    if (!canEditRoles) return;
    setMsg(null);
    const has = r.admin_capabilities.includes(cap);
    const next = has
      ? r.admin_capabilities.filter((c) => c !== cap)
      : [...r.admin_capabilities, cap];
    const { error } = await supabase.rpc('admin_set_capabilities', {
      p_user_id: r.user_id,
      p_capabilities: next,
    });
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    await supabase.rpc('audit_log', {
      p_action: 'admin_capabilities_changed',
      p_target_type: 'profile',
      p_target_id: r.user_id,
      p_metadata: { email: r.email, from: r.admin_capabilities, to: next } as any,
    });
    setMsg({ kind: 'success', text: `${r.email}: ${has ? 'revoked' : 'granted'} ${cap}` });
    setRows((prev) => prev.map((x) => x.user_id === r.user_id
      ? { ...x, admin_capabilities: next as Capability[], is_admin: x.is_super_admin || x.is_club_admin || next.length > 0 }
      : x));
  };

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Users"
        subtitle={canEditRoles
          ? 'Promote trusted teammates to club-admin or super-admin here.'
          : 'All users on the platform. Role changes are super-admin only.'}
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <form onSubmit={submitSearch} className="mb-5 flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, or phone"
            className="pl-9"
          />
          {q && (
            <button type="button" onClick={() => { setQ(''); load(null); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <Button type="submit" variant="secondary" icon={Search}>Search</Button>
      </form>

      <Section
        title={loading ? 'Loading users…' : `${rows.length} user${rows.length === 1 ? '' : 's'}`}
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={User}
            title="No users match that search"
            description="Try a different email, name, or phone fragment."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">User</th>
                <th className="text-left px-5 py-3 font-semibold">Contact</th>
                <th className="text-left px-5 py-3 font-semibold">ID verified</th>
                <th className="text-left px-5 py-3 font-semibold">Last sign-in</th>
                <th className="text-left px-5 py-3 font-semibold">Role</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const role = rowToRole(r);
                return (
                  <tr key={r.user_id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{r.full_name ?? '—'}</div>
                          <div className="text-xs text-gray-500 font-mono">{r.user_id.slice(0, 8)}…</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-sm flex items-center gap-1">
                        <Mail className="w-3 h-3 text-gray-400" /> {r.email}
                      </div>
                      {r.phone && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3 text-gray-400" /> {r.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {r.identity_verified_at
                        ? <Badge tone="green" icon={CheckCircle2}>Verified</Badge>
                        : <Badge tone="gray">Not verified</Badge>}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600">
                      {r.last_sign_in_at
                        ? new Date(r.last_sign_in_at).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1.5">
                        {role === 'super_admin'
                          ? <Badge tone="lime" icon={Crown}>Super admin</Badge>
                          : role === 'club_admin'
                            ? <Badge tone="blue" icon={ShieldCheck}>Club admin</Badge>
                            : r.admin_capabilities.length > 0
                              ? <Badge tone="blue" icon={ShieldCheck}>Scoped admin</Badge>
                              : <Badge tone="gray">Customer</Badge>}
                        {role !== 'super_admin' && r.admin_capabilities.length > 0 && (
                          <div className="flex flex-wrap gap-1 max-w-[14rem]">
                            {r.admin_capabilities.map((c) => {
                              const M = CAP_META[c];
                              return <Badge key={c} tone={M.tone} icon={M.icon}>{M.label}</Badge>;
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {canEditRoles && (
                        <div className="flex flex-col items-end gap-2">
                          <Select
                            value={role}
                            onChange={(e) => setRole(r, e.target.value as Role)}
                            className="w-36 h-8 text-xs"
                          >
                            <option value="none">Customer</option>
                            <option value="club_admin">Club admin</option>
                            <option value="super_admin">Super admin</option>
                          </Select>
                          {role !== 'super_admin' && (
                            <div className="flex gap-1">
                              {ALL_CAPS.map((c) => {
                                const has = r.admin_capabilities.includes(c);
                                const M = CAP_META[c];
                                return (
                                  <button
                                    key={c}
                                    type="button"
                                    title={`${has ? 'Revoke' : 'Grant'} ${M.label}`}
                                    onClick={() => toggleCapability(r, c)}
                                    className={`px-2 py-1 rounded text-[10px] font-medium border transition ${
                                      has
                                        ? 'bg-[#c6f806]/30 border-[#2d5016]/30 text-[#2d5016]'
                                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
                                    }`}
                                  >
                                    {M.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
